import { Ban, Eye, EyeClosed, Mail, Snowflake } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { forwardRef } from "react";

import { ColocaTooltip } from "@/shared/components/overlays/ColocaTooltip";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import type { CardAugmented } from "../constants/cardMocks.constants";
import { normalizeProduct, normalizeProvider, normalizeStatus } from "../utils/cardHelpers";
import { CardVisual } from "./CardVisual";
import { HolderCardLink } from "./HolderCardLink";
import { PocketCardLink } from "./PocketCardLink";

type CardDetailSidebarProps = {
  card: CardAugmented;
  viewUrl: string | null;
  isGeneratingUrl: boolean;
  canViewSensitive: boolean;
  onToggleReveal: () => void;
  onToggleFreeze: () => void;
  onDisable: () => void;
  isUpdatingStatus: boolean;
  onSendDataEmail: () => void;
  isSendDataEmailDisabled?: boolean;
};

type SidebarActionProps = {
  icon: LucideIcon;
  label: string;
  active?: boolean;
  danger?: boolean;
  disabled?: boolean;
  onClick: () => void;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "onClick" | "disabled" | "className">;

const SidebarAction = forwardRef<HTMLButtonElement, SidebarActionProps>(function SidebarAction(
  { icon: Icon, label, active = false, danger = false, disabled = false, onClick, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      {...rest}
      onClick={disabled ? undefined : onClick}
      aria-disabled={disabled}
      className={cn(
        "flex flex-col items-center justify-center gap-1.5 rounded-[var(--radius-md)] border bg-card p-2.5 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        disabled && "cursor-not-allowed opacity-50",
        active
          ? "border-primary bg-primary/10 text-primary"
          : danger
            ? "border-border text-destructive hover:bg-destructive/10"
            : "border-border text-foreground hover:bg-muted",
      )}
    >
      <Icon className="h-4 w-4" aria-hidden="true" />
      <span className="text-[11px] font-medium">{label}</span>
    </button>
  );
});

export const CardDetailSidebar = ({
  card,
  viewUrl,
  isGeneratingUrl,
  canViewSensitive,
  onToggleReveal,
  onToggleFreeze,
  onDisable,
  isUpdatingStatus,
  onSendDataEmail,
  isSendDataEmailDisabled = false,
}: CardDetailSidebarProps) => {
  const { t } = useTranslations("cards");

  const isRevealed = viewUrl !== null;
  const status = normalizeStatus(card.status || card.card_status);
  const isFrozen = status === "FROZEN";
  // Terminal — backend rejects reveal/freeze/disable on DISABLED cards.
  const isDisabled = status === "DISABLED";
  const product = normalizeProduct(card.card_type || card.product);
  const provider = normalizeProvider(card.provider || card.card_provider);
  const pocket = card.pocketRef;
  const holder = card.holder || card.card_holder_name || card.name || "—";
  const holderId = card.identity_id;
  const createdAt = card.created_at
    ? new Date(card.created_at).toLocaleDateString(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

  const infoRows = [
    {
      label: t("card_detail_info_type"),
      value: product === "VIRTUAL" ? t("product_virtual") : t("product_physical"),
    },
    {
      label: t("card_detail_info_brand"),
      value: t(`provider_${provider.toLowerCase()}` as never) || provider,
    },
    { label: t("card_detail_info_created"), value: createdAt },
  ];

  return (
    <aside className="space-y-4">
      <CardVisual
        card={card}
        viewUrl={viewUrl}
        isGeneratingUrl={isGeneratingUrl}
        frozen={isFrozen}
        disabled={isDisabled}
      />

      {pocket && (
        <PocketCardLink
          pocketId={pocket.id}
          name={pocket.name}
          color={pocket.color}
          icon={pocket.icon ?? null}
        />
      )}

      <div className="grid grid-cols-4 gap-2">
        {canViewSensitive ? (
          <SidebarAction
            icon={isRevealed ? EyeClosed : Eye}
            label={isRevealed ? t("card_detail_action_reveal_hide") : t("card_detail_action_reveal_show")}
            onClick={onToggleReveal}
            disabled={isGeneratingUrl || isDisabled}
          />
        ) : (
          <ColocaTooltip
            triggerRender={
              <SidebarAction
                icon={Eye}
                label={t("card_detail_action_reveal_show")}
                onClick={() => {}}
                disabled
              />
            }
          >
            {t("no_permission_contact_admin")}
          </ColocaTooltip>
        )}
        <SidebarAction
          icon={Snowflake}
          label={isFrozen ? t("card_detail_action_unfreeze") : t("card_detail_action_freeze")}
          active={isFrozen}
          onClick={onToggleFreeze}
          disabled={isUpdatingStatus || isDisabled}
        />
        {canViewSensitive ? (
          <SidebarAction
            icon={Mail}
            label={t("card_detail_action_email_cardholder")}
            onClick={onSendDataEmail}
            disabled={isUpdatingStatus || isDisabled || isSendDataEmailDisabled}
          />
        ) : (
          <ColocaTooltip
            triggerRender={
              <SidebarAction
                icon={Mail}
                label={t("card_detail_action_email_cardholder")}
                onClick={() => {}}
                disabled
              />
            }
          >
            {t("no_permission_contact_admin")}
          </ColocaTooltip>
        )}
        <SidebarAction
          icon={Ban}
          label={t("card_detail_action_cancel")}
          danger
          disabled={isUpdatingStatus || isDisabled}
          onClick={onDisable}
        />
      </div>

      <HolderCardLink holder={holder} holderId={holderId} email={card.email} />

      <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
        <div className="mb-3 text-sm font-medium text-foreground">{t("card_detail_info_title")}</div>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
          {infoRows.map((row) => (
            <div key={row.label} className="min-w-0">
              <dt className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                {row.label}
              </dt>
              <dd className="mt-0.5 truncate font-medium text-foreground">{row.value}</dd>
            </div>
          ))}
        </dl>
      </div>
    </aside>
  );
};
